/**
 * volumeRegime — bar-volume regime classification + smoothed-MA helper.
 *
 * JavaScript port of the user's NinjaTrader 8 indicator
 * `ultimate-volume-optimized.cs` located at `E:\AlphaWolfTrader\Code
 * Construction\Current Running Code\Indicators\AlphaWolfTrader\`.
 *
 * **What's ported in v1:** total bar volume + N-period SMA overlay + regime
 * classification.
 *
 * **What's deferred (v2+, needs tick data):** buy-volume / sell-volume split.
 * The NT C# indicator infers buy vs sell from each tick's price-vs-bid-ask
 * comparison; JournifiBars CSVs don't expose tick-level bid/ask, so we can
 * only port the total-volume + MA portion here. See
 * `docs/research/Coach_TopDown_Analysis.md` §6.3 for the workaround paths.
 *
 * Regime bands (default — overridable via opts):
 *   spike     — current > 1.5 × MA  (volume push, often confirms move)
 *   above-avg — 1.1 × MA ≤ current ≤ 1.5 × MA
 *   normal    — 0.8 × MA ≤ current < 1.1 × MA
 *   below-avg — 0.5 × MA ≤ current < 0.8 × MA
 *   dry-up    — current < 0.5 × MA  (no participation, often pre-reversal)
 *
 * Pure function, NaN-resilient (zero / non-finite volume produces 'normal'
 * by default with current=0).
 */

export const DEFAULT_OPTS = Object.freeze({
  period: 14,
  spikeMul: 1.5,
  aboveAvgMul: 1.1,
  normalLowerMul: 0.8,
  belowAvgMul: 0.5,
});

/**
 * Compute the simple moving average of bar volumes over `period` bars.
 *
 * @param {Array<{time:number, volume:number}>} bars
 * @param {number} [period=14]
 * @returns {Array<{time:number, value:number|null}>}
 *          Length === bars.length; warm-up bars hold null.
 */
export const computeVolumeMa = (bars, period = DEFAULT_OPTS.period) => {
  if (!Array.isArray(bars) || bars.length === 0) return [];
  const p = Math.max(1, Math.floor(Number(period)));
  const out = new Array(bars.length);
  let sum = 0;
  // Rolling window via subtract-add. NaN/non-finite volumes contribute 0 but
  // still count toward the window so the average doesn't lie about depth.
  const queue = [];
  for (let i = 0; i < bars.length; i++) {
    const t = Number(bars[i]?.time);
    const v = Number(bars[i]?.volume);
    const cleanV = Number.isFinite(v) && v >= 0 ? v : 0;
    queue.push(cleanV);
    sum += cleanV;
    if (queue.length > p) sum -= queue.shift();

    if (queue.length < p) {
      out[i] = { time: t, value: null };
    } else {
      out[i] = { time: t, value: sum / p };
    }
  }
  return out;
};

/**
 * Classify a single bar's volume against its trailing MA.
 *
 * @param {number} current  Current bar's volume.
 * @param {number} ma       MA of recent N bars (e.g. SMA(14)).
 * @param {Object} [opts]   Override regime multipliers.
 * @returns {'spike' | 'above-avg' | 'normal' | 'below-avg' | 'dry-up' | null}
 */
export const classifyVolumeRegime = (current, ma, opts = {}) => {
  if (!Number.isFinite(current) || !Number.isFinite(ma) || ma <= 0) return null;
  const spikeMul       = Number(opts.spikeMul       ?? DEFAULT_OPTS.spikeMul);
  const aboveAvgMul    = Number(opts.aboveAvgMul    ?? DEFAULT_OPTS.aboveAvgMul);
  const normalLowerMul = Number(opts.normalLowerMul ?? DEFAULT_OPTS.normalLowerMul);
  const belowAvgMul    = Number(opts.belowAvgMul    ?? DEFAULT_OPTS.belowAvgMul);

  const ratio = current / ma;
  if (ratio > spikeMul)       return 'spike';
  if (ratio >= aboveAvgMul)   return 'above-avg';
  if (ratio >= normalLowerMul) return 'normal';
  if (ratio >= belowAvgMul)   return 'below-avg';
  return 'dry-up';
};

/**
 * Look up the volume MA value at or just before a given timestamp.
 * Mirrors emaAtTime/alphaRsiAtTime shape.
 *
 * @param {Array<{time:number, value:number|null}>} series
 * @param {number} targetTime  Unix seconds.
 * @returns {number|null}
 */
export const volumeMaAtTime = (series, targetTime) => {
  if (!Array.isArray(series) || series.length === 0) return null;
  if (!Number.isFinite(targetTime)) return null;
  let last = null;
  for (const p of series) {
    if (!p) continue;
    if (p.time > targetTime) break;
    if (Number.isFinite(p.value)) last = p.value;
  }
  return last;
};

/**
 * Find the bar that contains or is the latest before a target timestamp.
 * Returns null if the target is before any bar.
 *
 * @param {Array<{time:number, volume?:number}>} bars
 * @param {number} targetTime  Unix seconds.
 * @returns {number|null}  Bar index, or null.
 */
const findBarIdxAtOrBefore = (bars, targetTime) => {
  if (!Array.isArray(bars) || bars.length === 0) return null;
  if (!Number.isFinite(targetTime)) return null;
  let found = null;
  for (let i = 0; i < bars.length; i++) {
    const t = Number(bars[i]?.time);
    if (!Number.isFinite(t)) continue;
    if (t <= targetTime) found = i;
    else break;
  }
  return found;
};

/**
 * Snapshot the full volume-regime state at a target timestamp. Returns the
 * exact shape consumed by the Top-Down per-TF state vector:
 *
 *   {
 *     current: number | null,
 *     ma: number | null,
 *     regime: 'spike' | 'above-avg' | 'normal' | 'below-avg' | 'dry-up' | null
 *   }
 *
 * @param {Array<{time:number, volume:number}>} bars
 * @param {number} targetTime  Unix seconds.
 * @param {Object} [opts]  Period + regime multipliers.
 */
export const volumeRegimeStateAtTime = (bars, targetTime, opts = {}) => {
  if (!Array.isArray(bars) || bars.length === 0) {
    return { current: null, ma: null, regime: null };
  }
  const idx = findBarIdxAtOrBefore(bars, targetTime);
  if (idx === null) {
    return { current: null, ma: null, regime: null };
  }
  // Take only up-to-and-including the target bar so MA reflects "as-of" not
  // forward-looking data.
  const upTo = bars.slice(0, idx + 1);
  const period = Number(opts.period ?? DEFAULT_OPTS.period);
  const series = computeVolumeMa(upTo, period);
  const maPoint = series[series.length - 1];

  const current = Number(bars[idx]?.volume);
  const cleanCurrent = Number.isFinite(current) && current >= 0 ? current : null;
  const ma = Number.isFinite(maPoint?.value) ? maPoint.value : null;
  return {
    current: cleanCurrent,
    ma,
    regime: classifyVolumeRegime(cleanCurrent, ma, opts),
  };
};
